import { Avatar, Skeleton, alpha, useTheme } from '@mui/material'

export const AVATAR_SIZE = 32

type UserAvatarProps = {
  /** Full name of the user, e.g. "Priya Nair". */
  name?: string | null
  /**
   * Render a placeholder circle instead of initials. Use while the user record
   * is still loading, so the avatar doesn't flash a color and an "U" for a name
   * that hasn't arrived.
   */
  loading?: boolean
}

// Theme palette colors used to give each user a stable identity color.
// Semantic colors (primary/secondary/error/neutral/grey) are left out so the
// avatars read as identity, not status.
const AVATAR_COLORS = [
  'blue',
  'green',
  'orange',
  'teal',
  'purple',
  'indigo',
  'blueGrey',
  'pink',
  'deepPurple',
] as const

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Deterministic index from the name so a given author always gets the same
// color across renders and sessions.
function getColorIndex(name: string, length: number): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % length
}

export default function UserAvatar({ name, loading }: UserAvatarProps) {
  const theme = useTheme()

  if (loading) {
    return (
      <Skeleton
        variant="circular"
        width={AVATAR_SIZE}
        height={AVATAR_SIZE}
        sx={{ flex: 'none' }}
      />
    )
  }

  const displayName = name?.trim() || 'Unknown'
  const color =
    theme.palette[
      AVATAR_COLORS[getColorIndex(displayName, AVATAR_COLORS.length)]
    ]
  return (
    <Avatar
      sx={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        fontSize: 13,
        fontWeight: 700,
        pt: '1px',
        bgcolor: color.main,
        // Partly transparent so the bubble color bleeds through the initials.
        color: alpha(color.contrastText, 0.75),
      }}
    >
      {getInitials(displayName)}
    </Avatar>
  )
}
